import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, X, Minus, Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";

export function CartDrawer() {
  const { items, setQty, remove, total, count } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const goCheckout = () => {
    setOpen(false);
    navigate(user ? "/app/checkout" : "/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button className="relative rounded-md border border-line p-2 text-ink hover:bg-rail" data-testid="open-cart">
          <ShoppingCart className="h-5 w-5" />
          {count > 0 && (
            <span className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full bg-brand text-[11px] font-bold text-white">
              {count}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent className="flex w-full flex-col sm:max-w-md">
        <SheetHeader><SheetTitle>Your cart</SheetTitle></SheetHeader>
        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center text-center text-muted">
            <ShoppingCart className="mb-3 h-10 w-10 text-line-strong" />
            Your cart is empty.
          </div>
        ) : (
          <>
            <div className="flex-1 space-y-4 overflow-y-auto py-4">
              {items.map((i) => (
                <div key={i.slug} className="flex gap-3" data-testid={`cart-item-${i.slug}`}>
                  {i.image ? (
                    <img src={i.image} alt={i.name} className="h-16 w-16 rounded-md border border-line object-cover" />
                  ) : (
                    <div className="grid h-16 w-16 shrink-0 place-items-center rounded-md border border-line bg-brand-wash text-brand"><ShoppingCart className="h-6 w-6" /></div>
                  )}
                  <div className="flex-1">
                    <div className="flex justify-between">
                      <p className="text-sm font-semibold text-ink">{i.name}</p>
                      <button onClick={() => remove(i.slug)} className="text-muted hover:text-danger"><X className="h-4 w-4" /></button>
                    </div>
                    <p className="text-sm text-muted">£{i.price.toFixed(2)}</p>
                    <div className="mt-2 flex items-center gap-2">
                      <button onClick={() => setQty(i.slug, i.qty - 1)} className="rounded border border-line p-1"><Minus className="h-3 w-3" /></button>
                      <span className="w-6 text-center text-sm">{i.qty}</span>
                      <button onClick={() => setQty(i.slug, i.qty + 1)} className="rounded border border-line p-1"><Plus className="h-3 w-3" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-line pt-4">
              <div className="mb-3 flex justify-between text-sm">
                <span className="text-muted">Subtotal</span>
                <span className="font-semibold text-ink">£{total.toFixed(2)}</span>
              </div>
              <button data-testid="cart-checkout-btn"
                onClick={goCheckout}
                className="w-full rounded-md bg-brand py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-dark">
                {user ? "Checkout" : "Checkout as guest"}
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
